import { useNavigation } from '@react-navigation/native'
import React, { ReactNode, forwardRef } from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import styled from 'styled-components/native'

import { UseNavigationType } from 'features/navigation/navigators/RootNavigator/types'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { useNumberOfLine } from 'shared/accessibility/helpers/zoomHelpers'
import { useGetHeaderHeightDS } from 'shared/header/useGetHeaderHeight'
import { Button } from 'ui/designSystem/Button/Button'
import { ArrowPrevious } from 'ui/svg/icons/ArrowPrevious'
import { Spacer, Typo } from 'ui/theme'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

interface Props {
  title?: string
  titleID?: string
  onGoBack?: () => void
  testID?: string
  shouldDisplayBackButton?: boolean
  RightButton?: ReactNode
  children?: ReactNode
  style?: StyleProp<ViewStyle>
  onLayout?: (event) => void
}

// Component naming: this component needs to be used with a PlaceHolder component
// that has the height of the header as it is an absolute view
export const PageHeaderWithoutPlaceholder = forwardRef<View, Props>(
  (
    {
      title,
      titleID,
      onGoBack,
      testID,
      shouldDisplayBackButton = true,
      RightButton = null,
      children,
      style,
      onLayout,
    },
    ref
  ) => {
    const { goBack } = useNavigation<UseNavigationType>()
    const headerHeight = useGetHeaderHeightDS()
    const numberOfLines = useNumberOfLine(2)
    return (
      <Header
        ref={ref}
        onLayout={onLayout}
        testID={testID}
        accessibilityRole={AccessibilityRole.HEADER}
        style={style}>
        <Spacer.TopScreen />
        <Container headerHeight={headerHeight}>
          <ButtonContainer positionInHeader="left" testID="back-button-container">
            {shouldDisplayBackButton ? (
              <Button
                iconButton
                variant="tertiary"
                color="neutral"
                icon={ArrowPrevious}
                onPress={onGoBack ?? goBack}
                accessibilityLabel="Revenir en arrière"
              />
            ) : null}
          </ButtonContainer>

          {title ? (
            <TitleContainer>
              <Title numberOfLines={numberOfLines} nativeID={titleID}>
                {title}
              </Title>
            </TitleContainer>
          ) : null}

          <ButtonContainer positionInHeader="right" testID="close-button-container">
            {RightButton}
          </ButtonContainer>
        </Container>

        {children}
      </Header>
    )
  }
)

PageHeaderWithoutPlaceholder.displayName = 'PageHeaderWithoutPlaceholder'

const Header = styled(View)(({ theme }) => ({
  zIndex: theme.zIndex.header,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.designSystem.color.background.default,
  borderBottomColor: theme.designSystem.separator.color.subtle,
  borderBottomWidth: 1,
}))

const TitleContainer = styled.View({ flex: 1, alignItems: 'center' })

const Title = styled(Typo.Title4).attrs<{ numberOfLines?: number }>(({ numberOfLines }) => ({
  numberOfLines,
  ...setTextSemantic('h1'),
}))({
  textAlign: 'center',
})

const Container = styled.View<{ headerHeight: number }>(({ theme, headerHeight }) => {
  return {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: headerHeight,
    width: '100%',
    paddingHorizontal: theme.contentPage.marginHorizontal,
  }
})

const ButtonContainer = styled.View<{ positionInHeader: 'left' | 'right' }>(
  ({ positionInHeader = 'left', theme }) => {
    const BACK_BUTTON_MAX_SIZE = theme.designSystem.size.spacing.xxxl
    const isLeftComponent = positionInHeader === 'left'
    const marginHorizontal = theme.designSystem.size.spacing.s
    return {
      alignItems: isLeftComponent ? 'flex-start' : 'center',
      justifyContent: isLeftComponent ? 'flex-start' : 'flex-end',
      minWidth: BACK_BUTTON_MAX_SIZE,
      marginLeft: isLeftComponent ? 0 : marginHorizontal,
      marginRight: isLeftComponent ? marginHorizontal : 0,
    }
  }
)
